/**
 * Streaming Chat Test UI
 */

import { APIClient, ChatMessage } from './api';

export class StreamingChatUI {
  private container: HTMLElement;
  private client: APIClient;
  private messagesContainer!: HTMLElement;
  private inputField!: HTMLTextAreaElement;
  private sendButton!: HTMLButtonElement;
  private clearButton!: HTMLButtonElement;
  private messages: ChatMessage[] = [];

  constructor(container: HTMLElement, client: APIClient) {
    this.container = container;
    this.client = client;
  }

  render(): void {
    this.container.innerHTML = `
      <div class="test-section">
        <h2>Streaming Chat Test</h2>
        <p class="description">Test real-time streaming AI chat with token-by-token responses</p>

        <div class="chat-container">
          <div class="messages" id="streaming-messages"></div>
          <div class="input-container">
            <textarea id="streaming-input" placeholder="Type your message..." rows="3"></textarea>
            <div class="button-group">
              <button id="streaming-send" class="btn btn-primary">Send</button>
              <button id="streaming-clear" class="btn btn-secondary">Clear Chat</button>
            </div>
          </div>
        </div>

        <div class="controls">
          <div class="control-group">
            <label>Model:</label>
            <select id="streaming-model">
              <option value="consenger/conversation">consenger/conversation</option>
              <option value="consenger/definition">consenger/definition</option>
              <option value="consenger/translation">consenger/translation</option>
              <option value="consenger/summary">consenger/summary</option>
              <option value="consenger/grammar">consenger/grammar</option>
              <option value="consenger/context">consenger/context</option>
            </select>
          </div>
          <div class="control-group">
            <label>Max Tokens:</label>
            <input type="number" id="streaming-max-tokens" value="2048" min="100" max="4096" step="100">
          </div>
          <div class="control-group">
            <label>Temperature:</label>
            <input type="number" id="streaming-temperature" value="0.7" min="0" max="1" step="0.1">
          </div>
        </div>
      </div>
    `;

    this.messagesContainer = document.getElementById('streaming-messages')!;
    this.inputField = document.getElementById('streaming-input')! as HTMLTextAreaElement;
    this.sendButton = document.getElementById('streaming-send')! as HTMLButtonElement;
    this.clearButton = document.getElementById('streaming-clear')! as HTMLButtonElement;

    this.setupEventListeners();
  }

  destroy(): void {
    this.container.innerHTML = '';
  }

  private setupEventListeners() {
    this.sendButton.addEventListener('click', () => this.sendMessage());
    this.clearButton.addEventListener('click', () => this.clearChat());

    this.inputField.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        this.sendMessage();
      }
    });
  }

  private async sendMessage() {
    const text = this.inputField.value.trim();
    if (!text) return;

    // Add user message
    this.messages.push({ role: 'user', content: text });
    this.appendMessage('user', text);
    this.inputField.value = '';

    // Disable input during streaming
    this.sendButton.disabled = true;
    this.inputField.disabled = true;

    // Create assistant message container
    const assistantMsg = this.createMessageElement('assistant', '');
    this.messagesContainer.appendChild(assistantMsg);
    const contentDiv = assistantMsg.querySelector('.message-content')!;

    try {
      const model = (document.getElementById('streaming-model') as HTMLSelectElement).value;
      const maxTokens = parseInt(
        (document.getElementById('streaming-max-tokens') as HTMLInputElement).value
      );
      const temperature = parseFloat(
        (document.getElementById('streaming-temperature') as HTMLInputElement).value
      );

      let fullResponse = '';

      for await (const event of this.client.streamChat(this.messages, maxTokens, temperature, model)) {
        if (event.type === 'token' && event.content) {
          fullResponse += event.content;
          contentDiv.textContent = fullResponse;
          this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
        } else if (event.type === 'error') {
          contentDiv.innerHTML = `<span class="error">Error: ${event.error?.message}</span>`;
          break;
        } else if (event.type === 'done') {
          break;
        }
      }

      // Store assistant response
      if (fullResponse) {
        this.messages.push({ role: 'assistant', content: fullResponse });
      }
    } catch (error) {
      contentDiv.innerHTML = `<span class="error">Error: ${
        error instanceof Error ? error.message : 'Unknown error'
      }</span>`;
    } finally {
      this.sendButton.disabled = false;
      this.inputField.disabled = false;
      this.inputField.focus();
    }
  }

  private appendMessage(role: 'user' | 'assistant', content: string) {
    const msgEl = this.createMessageElement(role, content);
    this.messagesContainer.appendChild(msgEl);
    this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
  }

  private createMessageElement(role: 'user' | 'assistant', content: string): HTMLElement {
    const msgEl = document.createElement('div');
    msgEl.className = `message message-${role}`;
    msgEl.innerHTML = `
      <div class="message-header">${role === 'user' ? 'You' : 'Assistant'}</div>
      <div class="message-content">${this.escapeHtml(content)}</div>
    `;
    return msgEl;
  }

  private clearChat() {
    this.messages = [];
    this.messagesContainer.innerHTML = '';
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
